import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import classNames from 'classnames';
import { connect } from 'react-redux';
import _get from 'lodash/get';

import { submitReview } from 'Redux/ducks/user-review';
import 'Components/ProductReviews/UserReviews/ReviewTextForm.scss';

const messages = defineMessages({
  display_name_placeholder: {
    id: 'reviewtextform.display_name_placeholder',
    defaultMessage: 'What shall we call you?',
  },
  summary_place_holder: {
    id: 'reviewtextform.summary_place_holder',
    defaultMessage: 'Summarise your review',
  },
  text_placeholder: {
    id: 'reviewtextform.text_placeholder',
    defaultMessage: 'Minimum 30 characters',
  },
});

const ReviewTextForm = ({
  slug,
  successCallBack,
  reviewData,
  productId,
  updateParentReviewData, // if parent needs to know text title and displayName updates,
  profileName,
  intl: { formatMessage },
  context,
}) => {
  const persistedUserReview = JSON.parse(localStorage.getItem('persistedUserReview')) || null;

  let persistedUserReviewText = '';
  const persistedUserReviewSlug = _get(persistedUserReview, 'slug');
  if (persistedUserReviewSlug === slug) persistedUserReviewText = _get(persistedUserReview, 'text');

  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [recommended, setRecommended] = useState(undefined);
  const [rating, setRating] = useState(undefined);

  const [textError, setTextError] = useState(false);
  const [recommendedError, setRecommendedError] = useState(false);
  const [ratingError, setRatingError] = useState(false);
  const [textLengthError, setTextLengthError] = useState(false);


  const {
    display_name: _displayName,
    title: _title,
    text: _text,
    recommended: _recommended,
    rating: _rating,
  } = reviewData;

  useEffect(() => {
    if (!_displayName && profileName) {
      setDisplayName(profileName);
    } else {
      setDisplayName(_displayName);
    }

    setText(_text || persistedUserReviewText);
    setTitle(_title);
    setRating(_rating);
    setRecommended(_recommended);
    if (_recommended !== undefined) setRecommendedError(false);
    if (_rating) setRatingError(false);
  }, [_displayName, _text, _title, _rating, _recommended, profileName, persistedUserReviewText]);

  const onInputChange = (value, setValue, name) => {
    if (textError) setTextError(false);
    if (textLengthError) setTextLengthError(false);
    if (recommendedError) setRecommendedError(false);
    if (ratingError) setRatingError(false);
    setValue(value);
    updateParentReviewData({ ...reviewData, [name]: value });

    if (name === 'text') {
      localStorage.setItem('persistedUserReview', JSON.stringify({ slug, text: value }));
    }
  };

  const onConfirmSubmitReview = async (e) => {
    e.preventDefault();
    updateParentReviewData({ ...reviewData, title, text, display_name: displayName });
    let incompleteForm = false;

    if (!text) {
      setTextError(true);
      incompleteForm = true;
    }
    if (text && text.length < 30) {
      setTextLengthError(true);
      incompleteForm = true;
    }
    if (recommended === undefined) {
      setRecommendedError(true);
      incompleteForm = true;
    }
    if (!rating) {
      setRatingError(true);
      incompleteForm = true;
    }

    if (incompleteForm) return false;

    const { submitted, published } = await submitReview({
      text,
      title,
      display_name: displayName,
      slug,
      product_id: productId,
      locale: window.locale,
      rating,
      recommended,
      context,
    });

    if (submitted) return successCallBack(published);
    return true;
  };

  const disableSubmit =
  (recommended === undefined || rating === undefined || !text || text.length < 30);

  return (
    <form className="review-text-form" onSubmit={onConfirmSubmitReview}>
      {(ratingError || recommendedError) &&
      <div className="rating-and-recommeneded-container">
        <div className="overall-rating-container">
          {ratingError &&
            <div className="invalid-field">
              <FormattedMessage
                id="reviewtextform.pleaseenterarating"
                defaultMessage="Please give this game an overall rating"
              />
            </div>
          }
        </div>
        <div className="recommended-container">
          {recommendedError &&
            <div className="invalid-field">
              <FormattedMessage
                id="reviewtextform.pleaseratethisgame"
                defaultMessage="Please tell us if you would recommend this game"
              />
            </div>
          }
        </div>
      </div>
      }

      <div className="form-group">
        <label htmlFor="displayName" className="col-form-label col-form-label-lg">
          <FormattedMessage id="reviewtextform.yourdisplayname" defaultMessage="Display Name" />
        </label>
        <input
          id="displayName"
          type="text"
          name="display_name"
          maxLength="40"
          className="form-control"
          aria-label="Display Name"
          autoComplete="nickname"
          value={displayName}
          placeholder={formatMessage(messages.display_name_placeholder)}
          onChange={e => onInputChange(e.target.value, setDisplayName, e.target.name)}
        />
      </div>

      <div className="form-group">
        <label htmlFor="reviewBody" className="col-form-label col-form-label-lg">
          <FormattedMessage id="reviewtextform.whatdidyouthinkofthegame" defaultMessage="What did you think of the game?" />
        </label>
        <textarea
          id="reviewBody"
          name="text"
          type="text"
          rows="4"
          className={classNames('form-control', { 'input-error': textError })}
          maxLength="5000"
          minLength="30"
          aria-label="Review Text"
          value={text}
          autoComplete="off"
          placeholder={formatMessage(messages.text_placeholder)}
          onChange={e => onInputChange(e.target.value, setText, e.target.name)}
        />
        <div className="ReviewTextForm__textArea__count text-end">
          {text.length} / 5000
        </div>
        {textError &&
          <div className="invalid-field">
            <FormattedMessage
              id="reviewtextform.pleaseenteryourreview"
              defaultMessage="Please enter your review"
            />
          </div>
        }

        {textLengthError &&
          <div className="invalid-field">
            <FormattedMessage
              id="reviewtextform.yourreviewmustbemore"
              defaultMessage="Your review must be more than 30 characters"
            />
          </div>
        }
      </div>

      <div className="form-group">
        <label htmlFor="reviewSummary" className="col-form-label col-form-label-lg">
          <FormattedMessage id="reviewtextform.reviewtitle" defaultMessage="Review Summary (Optional)" />
        </label>
        <input
          id="reviewSummary"
          type="text"
          name="summry"
          maxLength="100"
          className="form-control"
          aria-label="Review Summary"
          value={title}
          placeholder={formatMessage(messages.summary_place_holder)}
          onChange={e => onInputChange(e.target.value, setTitle, e.target.name)}
        />
      </div>

      <div className="form-group form-button-container">
        <button
          disabled={disableSubmit}
          type="submit"
          value="Submit"
          className="btn btn-primary confirm-continue-button"
        >
          <FormattedMessage
            id="reviewtextform.submitreview"
            defaultMessage="Submit Review"
          />
        </button>
      </div>
    </form>
  );
};


ReviewTextForm.propTypes = {
  slug: PropTypes.string,
  successCallBack: PropTypes.func,
  reviewData: PropTypes.shape({}),
  productId: PropTypes.string,
  updateParentReviewData: PropTypes.func,
  profileName: PropTypes.string,
  context: PropTypes.string,
};

ReviewTextForm.defaultProps = {
  slug: '',
  productId: '',
  reviewData: {
    text: '',
    title: '',
    display_name: '',
  },
  successCallBack: () => {},
  updateParentReviewData: () => {},
  profileName: '',
  context: '',
};


const mapStateToProps = ({
  auth: { profile_name: profileName },
}) => ({
  profileName,
});

export default connect(mapStateToProps)(injectIntl(ReviewTextForm));
